import * as React from "react";
import { view } from "@orderofchaos/mvvm";
import { Layout } from "@/components/Layout/Layout";
import styles from "./EditProductPage.module.css";
import Button, { ButtonDesign } from "@/components/Button/Button";
import FieldSet from "@/components/FieldSet/FieldSet";
import Input from "@/components/Input/Input";
import { PickADayInput } from "@/components/PickADay/PickADayInput";
import { TypeTagsInput } from "@/components/TypeTagsInput/TypeTagsInput";
import { ProductPhotoSlider } from "@/components/ProductPhotoSlider/ProductPhotoSlider";
import Icon, { IconSize } from "@/components/Icon/Icon";
import { QrScannerOverlay } from "@/components/QrScannerOverlay/QrScannerOverlay";
import { BoxReferenceInput } from "@/components/BoxReferenceInput/BoxReferenceInput";
import type { Product } from "@/stores/Product";
import {
  EditProductPageViewModel,
  type EditProductPageViewProps,
} from "./EditProductPageViewModel";
import { Measures } from "@/types";

interface EditProductPageProps {
  product: Product;
}

export const EditProductPage: React.FC<EditProductPageProps> = view(
  EditProductPageViewModel,
)<EditProductPageViewProps>(({ viewModel }) => {
  return (
    <Layout title={viewModel.title}>
      <div className={styles.root}>
        <div className={styles.photoHero}>
          <ProductPhotoSlider
            photo={viewModel.photo}
            photos={viewModel.photos}
            alt={viewModel.name || "preview"}
            className={styles.imageSlider}
            trackClassName={styles.imageSliderTrack}
            imageClassName={styles.image}
            emptyClassName={styles.image}
            eagerFirstImage
            onActiveIndexChange={viewModel.onPhotoSliderActiveIndexChange}
          />
          <div className={styles.photoControls}>
            <label
              className={`${styles.photoAction} ${!viewModel.canAddMorePhotos ? styles.photoActionDisabled : ""}`}
              title={
                viewModel.canAddMorePhotos
                  ? "Добавить фото"
                  : "Достигнут лимит фото для товара"
              }
            >
              <input
                type="file"
                accept="image/*"
                onChange={(event) => void viewModel.onPhotoFileChange(event)}
                className={styles.photoUploadInput}
                disabled={!viewModel.canAddMorePhotos}
              />
              <Icon name="Plus" size={IconSize.medium} />
            </label>
            <button
              type="button"
              className={styles.photoAction}
              onClick={viewModel.removeActivePhoto}
              disabled={!viewModel.canRemoveActivePhoto}
              title="Удалить текущее фото"
            >
              <Icon name="Minus" size={IconSize.medium} />
            </button>
          </div>
        </div>
        <div className={styles.photoMeta}>
          <span className={styles.photoCounter}>
            {viewModel.photoCounterLabel}
          </span>
          {viewModel.isPhotoLoading && (
            <span className={styles.hint}>загружаем изображение...</span>
          )}
        </div>

        <FieldSet title={"наименование"}>
          <Input value={viewModel.name} onChange={viewModel.onNameChange} />
        </FieldSet>

        <FieldSet title={"описание"}>
          <Input
            value={viewModel.description}
            onChange={viewModel.onDescriptionChange}
          />
        </FieldSet>

        <FieldSet title={"photo"}>
          <p className={styles.hint}>
            Управляйте фото из превью сверху: + добавить, - удалить текущее.
          </p>
        </FieldSet>

        <FieldSet title={"типы (#теги)"}>
          <TypeTagsInput
            tags={viewModel.typeTags}
            suggestions={viewModel.typeTagSuggestions}
            inputValue={viewModel.typeTagInput}
            onInputValueChange={viewModel.setTypeTagInput}
            onAddTags={viewModel.addTypeTags}
            onRemoveTag={viewModel.removeTypeTag}
            datalistId="edit-product-type-tags"
            placeholder="#еда #молочка"
          />
        </FieldSet>

        <FieldSet title={"коробка (опционально)"}>
          <BoxReferenceInput
            value={viewModel.parentId}
            options={viewModel.parentIdSuggestions}
            onValueChange={viewModel.setParentIdInput}
            placeholder="Например: Холодильник"
            searchPlaceholder="Найти коробку"
            isLoading={viewModel.isReferenceLoading}
            disabled={viewModel.isSubmitting}
            allowEmpty
          />
        </FieldSet>

        <FieldSet title={"QR-код (опционально)"}>
          <p className={styles.hint}>
            Текущий QR:{" "}
            <strong>
              {viewModel.boundQrPayload
                ? viewModel.boundQrPayload
                : "не привязан"}
            </strong>
          </p>
          <div className={styles.qrControls}>
            <Button
              design={
                viewModel.isQrScannerOpen
                  ? ButtonDesign.secondary
                  : ButtonDesign.basic
              }
              onClick={viewModel.toggleQrScanner}
              disabled={viewModel.isSubmitting || viewModel.isQrScanResolving}
            >
              <Icon name="QR" size={IconSize.small} />
              {viewModel.isQrScannerOpen
                ? "Остановить сканирование"
                : "Сканировать QR"}
            </Button>
          </div>

          <p className={styles.hint}>
            {viewModel.qrPayloadInput
              ? `Новый QR: ${viewModel.qrPayloadInput}`
              : "Отсканируйте новый QR, если хотите заменить текущую привязку."}
          </p>
          {viewModel.isQrScanResolving && (
            <p className={styles.hint}>проверяем QR...</p>
          )}
          {viewModel.qrBindingError && (
            <p className={styles.error}>{viewModel.qrBindingError}</p>
          )}
        </FieldSet>
        <QrScannerOverlay
          isOpen={viewModel.isQrScannerOpen}
          title="Замена QR товара"
          description="Наведите камеру на новый QR. После проверки он заменит текущую привязку при сохранении товара."
          delay={viewModel.qrScannerDelay}
          selectedPayload={viewModel.qrPayloadInput}
          selectedLabel="Новый QR"
          error={viewModel.qrBindingError}
          isResolving={viewModel.isQrScanResolving}
          idleText="Наведите камеру на QR-код из партии печати."
          onClose={viewModel.toggleQrScanner}
          onError={viewModel.onQrScanError}
          onScan={viewModel.onQrScan}
        />

        <div className={styles.dates}>
          <FieldSet title={"дата производства"}>
            <PickADayInput
              value={viewModel.manufacturingDate}
              onChangeValue={viewModel.setManufacturingDateValue}
              locale="ru"
              disabled={viewModel.isSubmitting}
            />
          </FieldSet>
          <FieldSet title={"срок годности"}>
            <PickADayInput
              value={viewModel.expiryDate}
              onChangeValue={viewModel.setExpiryDateValue}
              locale="ru"
              disabled={viewModel.isSubmitting}
            />
          </FieldSet>
        </div>

        <FieldSet title={"единица измерения"}>
          <div className={styles.measureButtons}>
            <Button
              design={
                viewModel.measure === Measures.quantity
                  ? ButtonDesign.primary
                  : ButtonDesign.basic
              }
              onClick={() => viewModel.setMeasure(Measures.quantity)}
            >
              quantity
            </Button>
            <Button
              design={
                viewModel.measure === Measures.weight
                  ? ButtonDesign.primary
                  : ButtonDesign.basic
              }
              onClick={() => viewModel.setMeasure(Measures.weight)}
            >
              weight
            </Button>
            <Button
              design={
                viewModel.measure === Measures.liters
                  ? ButtonDesign.primary
                  : ButtonDesign.basic
              }
              onClick={() => viewModel.setMeasure(Measures.liters)}
            >
              liters
            </Button>
          </div>
        </FieldSet>

        <FieldSet title={"количество"}>
          <Input
            type="number"
            value={viewModel.quantityInput}
            onChange={viewModel.onQuantityChange}
            placeholder="1"
          />
        </FieldSet>

        {viewModel.isReferenceLoading && (
          <p className={styles.hint}>загружаем коробки и теги...</p>
        )}
        {viewModel.referenceError && (
          <p className={styles.error}>{viewModel.referenceError}</p>
        )}
        {viewModel.error && <p className={styles.error}>{viewModel.error}</p>}

        <div className={styles.buttons}>
          <Button
            design={ButtonDesign.primary}
            onClick={() => void viewModel.submit()}
            disabled={!viewModel.canSubmit}
            isLoading={viewModel.isSubmitting}
          >
            сохранить
          </Button>
          <Button
            design={ButtonDesign.basic}
            onClick={viewModel.resetFromProduct}
            disabled={viewModel.isSubmitting}
          >
            сбросить
          </Button>
        </div>

        <Button design={ButtonDesign.ghost} onClick={viewModel.goBack}>
          назад к продукту
        </Button>
      </div>
    </Layout>
  );
});
